import type { Target } from '../../models/TimerTarget';

import { eachDayOfInterval, parseISO, subDays } from 'date-fns';

import { Api } from '../../api';
import { Model } from '../../models';
import { formatAsDate } from '../../util';

/**
 * How much time the activity bar spans end to end.  Every row is drawn against
 * this rather than stretching to its own total, so one person's day can be read
 * against another's.
 */
export const FULL_DAY_MINUTES = 8 * 60;

/** How far back the day picker reaches. Anything older is the Time Log's job. */
export const SELECTABLE_DAYS = 14;

/**
 * One piece of work in someone's day.  Time logged more than once against the
 * same thing is a single segment, so the bar reads as a list of work rather
 * than a list of entries.
 */
export type ActivitySegment = {
  key: string;
  label: string;
  minutes: number;
  /** Drives the colour, and is null only for the deprecated task target. */
  companyId: string | null;
};

export type UserActivity = {
  user: Api.User.BaseView;
  /** Banked on the day, not counting whatever is still on the clock. */
  minutes: number;
  /** In the order the work was first logged, so the bar reads as the day did. */
  segments: Array<ActivitySegment>;
  running: { target: Target; startedAt: string } | null;
};

/**
 * Everyone's day, longest first, with people who have logged nothing at the
 * bottom.  They keep a row rather than dropping out: an empty row says nobody
 * has logged anything, where a missing one just looks like data not arriving.
 *
 * `entries` is the day as one list, banked and still running together. A past
 * day simply has nothing running in it.
 */
export function buildDayActivity(
  users: ReadonlyArray<Api.User.BaseView>,
  entries: ReadonlyArray<Api.WorkDone.V_List>
): Array<UserActivity> {
  const byUser = groupByUser(entries, (entry) => entry.user.id);

  return users
    .map((user) => {
      const theirs = byUser.get(user.id) ?? [];
      const logged = Model.WorkDone.filterLogged(theirs);

      return {
        user,
        minutes: Model.WorkDone.sumMinutes(logged),
        segments: toSegments(logged),
        running: toRunning(theirs.find(Model.WorkDone.isRunning)),
      };
    })
    .sort(
      (a, b) =>
        b.minutes - a.minutes ||
        `${a.user.firstName} ${a.user.lastName}`.localeCompare(
          `${b.user.firstName} ${b.user.lastName}`
        )
    );
}

/**
 * The work worth naming under the bar, longest first.  The rest is left to the
 * bar itself, which already shows it.
 */
export function getTopSegments(
  activity: UserActivity,
  limit: number
): { shown: Array<ActivitySegment>; remaining: number } {
  const byLongest = [...activity.segments].sort(
    (a, b) => b.minutes - a.minutes
  );

  return {
    shown: byLongest.slice(0, limit),
    remaining: Math.max(byLongest.length - limit, 0),
  };
}

/**
 * The days the activity can be shown for, today first, as `yyyy-MM-dd`.
 */
export function getSelectableDates(today: string): Array<string> {
  const end = parseISO(today);

  return eachDayOfInterval({ start: subDays(end, SELECTABLE_DAYS - 1), end })
    .reverse()
    .map((day) => formatAsDate(day));
}

function groupByUser<T>(
  entries: ReadonlyArray<T>,
  getUserId: (entry: T) => string
): Map<string, Array<T>> {
  const byUser = new Map<string, Array<T>>();

  for (const entry of entries) {
    const userId = getUserId(entry);
    const existing = byUser.get(userId);
    if (existing) existing.push(entry);
    else byUser.set(userId, [entry]);
  }

  return byUser;
}

/**
 * Entries arrive newest first, so they are walked backwards to lay the day out
 * left to right.
 */
function toSegments(
  entries: ReadonlyArray<Api.WorkDone.V_List>
): Array<ActivitySegment> {
  const byTarget = new Map<string, ActivitySegment>();

  for (const entry of [...entries].reverse()) {
    const target = Model.TimerTarget.fromWorkDone(entry);
    const key = target?.iri ?? entry.id;
    const existing = byTarget.get(key);

    if (existing) {
      existing.minutes += entry.minutes ?? 0;
      continue;
    }

    byTarget.set(key, {
      key,
      label: target?.title ?? 'Untitled work',
      minutes: entry.minutes ?? 0,
      companyId: target?.company?.id ?? null,
    });
  }

  return [...byTarget.values()];
}

function toRunning(
  entry: Api.WorkDone.V_List | undefined
): UserActivity['running'] {
  if (!entry?.loggingStartedAt) return null;

  const target = Model.TimerTarget.fromWorkDone(entry);
  if (!target) return null;

  return { target, startedAt: entry.loggingStartedAt };
}
